import { AuditChecklistItem, ChecklistItemStatus, AuditStatus } from '@fsp/types';
import {
  CheckCircleOutlined,
  RadioButtonUncheckedOutlined,
  WarningAmberOutlined,
  VisibilityOutlined,
  ErrorOutlined,
  ExpandMore,
} from '@mui/icons-material';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Stack,
  Typography,
  Chip,
  TextField,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useAudit } from '../../../../contexts/AuditContext';
import { useSnackbar } from 'notistack';
import { getChecklistItemStatusIcon } from '../utils/auditHelpers';

const getStatusIcon = (status: ChecklistItemStatus, hasUnresolvedCA: boolean) => {
  // For non-conformities with unresolved corrective actions, show error icon
  if (status === ChecklistItemStatus.NON_CONFORMITY && hasUnresolvedCA) {
    return <ErrorOutlined sx={{ fontSize: 18, color: 'error.main' }} />;
  }
  // Otherwise use the standard status icon
  return getChecklistItemStatusIcon(status);
};

interface ChecklistItemAccordionProps {
  item: AuditChecklistItem;
  disabled?: boolean;
}

const ChecklistItemAccordion: React.FC<ChecklistItemAccordionProps> = ({
  item,
  disabled = false,
}) => {
  const { updateItem } = useAudit();
  const { enqueueSnackbar } = useSnackbar();

  const [status, setStatus] = useState<ChecklistItemStatus>(item.status);
  const [auditorNotes, setAuditorNotes] = useState(item.auditorNotes || '');
  const [expanded, setExpanded] = useState(false);

  // Update local state when item changes
  useEffect(() => {
    setStatus(item.status);
    setAuditorNotes(item.auditorNotes || '');
  }, [item]);

  const handleSave = async () => {
    try {
      const updates: Partial<AuditChecklistItem> = {
        status,
        auditorNotes: auditorNotes || undefined,
      };

      await updateItem(item.id, updates);
      enqueueSnackbar('Checklist item updated', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to update checklist item', { variant: 'error' });
    }
  };

  const handleStatusChange = async (newStatus: ChecklistItemStatus) => {
    setStatus(newStatus);
    // Auto-save on status change
    try {
      const updates: Partial<AuditChecklistItem> = {
        status: newStatus,
        auditorNotes: auditorNotes || undefined,
      };

      await updateItem(item.id, updates);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to update checklist item', { variant: 'error' });
    }
  };

  const handleNotesBlur = () => {
    // Auto-save on blur if notes changed
    if (auditorNotes !== (item.auditorNotes || '')) {
      handleSave();
    }
  };

  const hasUnresolvedCA =
    item.status === ChecklistItemStatus.NON_CONFORMITY && !item.isCorrectiveActionResolved;

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      elevation={0}
      disableGutters
      sx={{
        '&:before': { display: 'none' },
        border: 0,
        borderBottom: 1,
        borderColor: 'divider',
        borderRadius: 0,
        '&.MuiAccordion-root': {
          margin: 0,
        },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', pr: 1 }}>
          {getStatusIcon(status, hasUnresolvedCA)}
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {item.requirementText}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pb: 2 }}>
        <Stack spacing={2}>
          {/* Category Badge */}
          {/* <Chip
            label={item.category}
            color="primary"
            size="small"
            sx={{ alignSelf: 'flex-start' }}
          /> */}

          {/* Status Selector */}
          <ToggleButtonGroup
            value={status}
            exclusive
            onChange={(_e, newStatus) => {
              if (newStatus !== null) {
                handleStatusChange(newStatus);
              }
            }}
            disabled={disabled}
            fullWidth
            size="small"
          >
            <Tooltip title="Not Checked">
              <ToggleButton value={ChecklistItemStatus.NOT_CHECKED}>
                <RadioButtonUncheckedOutlined fontSize="small" />
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Satisfactory">
              <ToggleButton value={ChecklistItemStatus.SATISFACTORY}>
                <CheckCircleOutlined fontSize="small" />
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Non-Conformity">
              <ToggleButton value={ChecklistItemStatus.NON_CONFORMITY}>
                <WarningAmberOutlined fontSize="small" />
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Observation">
              <ToggleButton value={ChecklistItemStatus.OBSERVATION}>
                <VisibilityOutlined fontSize="small" />
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>

          {/* Auditor Notes */}
          <TextField
            label="Auditor Notes"
            multiline
            rows={3}
            value={auditorNotes}
            onChange={(e) => setAuditorNotes(e.target.value)}
            onBlur={handleNotesBlur}
            disabled={disabled}
            placeholder="Add notes about your findings..."
            size="small"
          />

          {/* Metadata */}
          {item.checkedAt && item.checkedBy && (
            <Typography variant="caption" color="text.secondary">
              Last checked on {new Date(item.checkedAt).toLocaleString()}
            </Typography>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const CategorySection: React.FC<{
  category: string;
  items: AuditChecklistItem[];
  disabled?: boolean;
}> = ({ category, items, disabled = false }) => {
  const checkedCount = items.filter((i) => i.status !== ChecklistItemStatus.NOT_CHECKED).length;
  const totalCount = items.length;
  const percentComplete = totalCount > 0 ? Math.round((checkedCount / totalCount) * 100) : 0;
  const hasUnresolvedCAs = items.some(
    (i) => i.status === ChecklistItemStatus.NON_CONFORMITY && !i.isCorrectiveActionResolved
  );

  return (
    <Box>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: 'background.default',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            {category}
          </Typography>
          {hasUnresolvedCAs && (
            <Chip label="CA" size="small" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
          )}
        </Stack>
        <Box sx={{ mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {checkedCount}/{totalCount} checked
          </Typography>
          <LinearProgress
            variant="determinate"
            value={percentComplete}
            sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
          />
        </Box>
      </Box>
      {items.map((item) => (
        <ChecklistItemAccordion key={item.id} item={item} disabled={disabled} />
      ))}
    </Box>
  );
};

export const AuditChecklistSidebar: React.FC = () => {
  const { getCategories, getItemsByCategory, getSummary, currentAudit } = useAudit();

  const categories = getCategories();
  const summary = getSummary();
  const isReadOnly = currentAudit?.status === AuditStatus.ARCHIVED;

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header with overall progress */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" gutterBottom>
          Audit Checklist
        </Typography>
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              Progress: {summary.percentComplete}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {summary.checkedItems}/{summary.totalItems}
            </Typography>
          </Stack>
          <LinearProgress variant="determinate" value={summary.percentComplete} />
          {summary.unresolvedCorrectiveActions > 0 && (
            <Typography variant="caption" color="warning.main">
              {summary.unresolvedCorrectiveActions} unresolved Corrective Action
              {summary.unresolvedCorrectiveActions > 1 ? 's' : ''}
            </Typography>
          )}
        </Stack>
      </Box>

      {/* Scrollable checklist */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {categories.map((category) => (
          <CategorySection
            key={category}
            category={category}
            items={getItemsByCategory(category)}
            disabled={isReadOnly}
          />
        ))}
      </Box>
    </Box>
  );
};

export default AuditChecklistSidebar;
